import { Link, useLocation } from "wouter";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Badge } from "@/components/ui/badge";

type NavItemType = {
  href: string;
  label: string;
  icon: string;
  disabled?: boolean;
};

const navItems: NavItemType[] = [
  { href: "/dashboard", label: "Dashboard", icon: "fas fa-home" },
  { href: "/players", label: "Player Database", icon: "fas fa-database" },
  { href: "/updates", label: "Updates", icon: "fas fa-bell" },
  { href: "/shortlists", label: "Shortlists", icon: "fas fa-star" },
  { href: "/team", label: "Team", icon: "fas fa-users" },
  { href: "/email-settings", label: "Email Settings", icon: "fas fa-envelope" },
  { href: "/email-management", label: "Email Management", icon: "fas fa-chart-bar"},
  { href: "/feedback", label: "Feedback", icon: "fas fa-comment-alt" },
  { href: "/profile", label: "Profile", icon: "fas fa-user" },
];

interface SidebarProps {
  isMobile?: boolean;
  onClose?: () => void;
}

export default function Sidebar({ isMobile = false, onClose }: SidebarProps) {
  const [location] = useLocation();
  const { user, logout } = useAuth();
  
  // Fetch update count
  const { data: updateCount } = useQuery({
    queryKey: ["player-updates-count"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/players/updates/count");
      if (!response.ok) {
        return { count: 0 };
      }
      const data = await response.json();
      return data as { count: number };
    },
    refetchInterval: 30000, // Refresh every 30 seconds
  });
  
  if (!user) return null;
  
  // Extract initials for avatar
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };
  
  const userInitials = getInitials(user.name || 'User');

  const handleNavigation = () => {
    if (isMobile && onClose) {
      onClose(); // Close mobile menu after navigation
    }
  };

  const handleLogout = () => {
    logout();
    if (isMobile && onClose) {
      onClose();
    }
  };
  
  // Use different wrapper classes based on mobile/desktop
  const WrapperComponent = isMobile ? 'div' : 'aside';
  const wrapperClasses = isMobile 
    ? "flex flex-col h-full" 
    : "hidden md:flex md:flex-shrink-0 h-full";
  
  return (
    <WrapperComponent className={wrapperClasses}>
      <div className={`flex flex-col w-64 border-r border-cyan-200 bg-gradient-to-b from-white to-cyan-50 backdrop-blur-sm h-full`}>
        <div className="flex-shrink-0 px-4 py-4 flex items-center justify-center border-b border-cyan-200">
          <h1 className="text-xl font-bold bg-gradient-to-r from-cyan-600 to-teal-600 bg-clip-text text-transparent">
            Bloom
          </h1>
        </div>
        
        {/* User profile section */}
        <div className="flex-shrink-0 px-4 py-4 border-b border-cyan-200">
          <div className="flex items-center">
            <div className="flex-shrink-0 h-10 w-10 rounded-full bg-gradient-to-r from-cyan-500 to-teal-500 flex items-center justify-center text-white shadow-lg">
              <span className="font-medium text-sm">{userInitials}</span>
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-800">{user.name}</p>
              <p className="text-xs text-gray-600">{user.school || 'School not set'}</p>
            </div>
          </div>
        </div>
        
        {/* Navigation Links */}
        <nav className="mt-5 px-2 space-y-1 flex-1 overflow-y-auto">
          {navItems.map((item) => {
            const isActive = location === item.href;
            
            // If the item is disabled, render a div instead of a Link
            if (item.disabled) {
              return (
                <div
                  key={item.href}
                  className="text-gray-400 cursor-not-allowed group flex items-center px-3 py-2 text-sm font-medium rounded-md"
                  title="Coming soon"
                >
                  <i className={`${item.icon} mr-3 w-6`}></i>
                  {item.label}
                </div>
              );
            }
            
            // Otherwise render a normal link
            return (
              <Link 
                key={item.href}
                href={item.href}
                onClick={handleNavigation}
                className={`
                  ${isActive 
                    ? 'bg-gradient-to-r from-cyan-100 to-teal-100 text-cyan-700 border-l-4 border-cyan-500 shadow-sm' 
                    : 'text-gray-700 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-teal-50 hover:text-cyan-600'
                  } 
                  group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-all duration-200
                `}
              >
                <i className={`${item.icon} mr-3 w-6`}></i>
                <span className="flex-1">{item.label}</span>
                {item.href === "/updates" && updateCount && updateCount.count > 0 && (
                  <Badge className="ml-auto" variant="default">
                    {updateCount.count}
                  </Badge>
                )}
              </Link>
            );
          })}
        </nav>
        
        {/* Logout button */}
        <div className="flex-shrink-0 border-t border-cyan-200 p-4">
          <button 
            onClick={handleLogout} 
            className="flex items-center text-sm text-gray-700 hover:text-cyan-600 hover:bg-gradient-to-r hover:from-cyan-50 hover:to-teal-50 px-2 py-1 rounded transition-all duration-200 w-full"
          >
            <i className="fas fa-sign-out-alt mr-3"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>
    </WrapperComponent>
  );
}